import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

//models
import IUnite from "../../models/IUnite";
import Guid from "../../models/Guid";

//hooks
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import useApi from "../../hooks/useApi";

//components
import { useDateInput } from '../../components/inputs';

//devextrem
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { FormRef, GroupItem, Label, SimpleItem } from "devextreme-react/form";
import { FieldDataChangedEvent } from "devextreme/ui/form";
import DataGrid, { Column, Editing, Lookup } from 'devextreme-react/data-grid';
import { Button, ColorBox, FileUploader, SelectBox } from 'devextreme-react';

//base
import { FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar } from '../base';
import useReservationCouleur from '../../components/selectbox/ReservationCouleur';

//data
// import useLoadDataUnite from './useLoadDataUnite';
import useLookUpBase from '../../components/selectbox/LookupBase';
import FormSysteme from '../../components/forms/form-systeme';
import GestionPlans from '../gestion-plans/gestion-plans';
import GestionTypesUnites from '../gestion-types-unites/gestion-types-unites';
import GestionFolios from '../gestion-folios/gestion-folios';
import GestionSecteurs from '../gestion-secteurs/gestion-secteurs';
import GestionPointsEau from '../gestion-points-eau/gestion-points-eau';
import GestionMessages from '../gestion-messages/gestion-messages';
import GestionCatalogueProduits from '../gestion-catalogue-produits/gestion-catalogue-produits';
import GestionCategoriesUnites from '../gestion-categories-unites/gestion-categories-unites';
import GestionTypesServices from '../gestion-types-services/gestion-types-services';
import GestionTypesEquipements from '../gestion-types-equipements/gestion-types-equipements';
import IGestionUnite from '../../models/IGestionUnite';
import IUniteImage from '../../models/IUniteImage';
import IUniteHorsService from '../../models/IUniteHorsService';
import IUniteService from '../../models/IUniteService';
import IUniteEquipment from '../../models/IUniteEquipment';
import IUniteForfait from '../../models/IUniteForfait';
import IUnitePersonneTrancheAge from '../../models/IUnitePersonneTrancheAge';
import ConfigurationTranchesAges from '../configuration/configuration-tranches-ages';
import { alignment } from 'devexpress-reporting/scopes/reporting-designer-controls-metadata';
import { uniteColorFieldToColorBoxValue } from "../../utils/windowsColorRef";
import { useCouleurInput } from '../../components/inputs';
import useEnum from '../../hooks/useEnum';
import {
  packUniteImageWithFileName,
  parseUniteImagePayload,
  toUniteImageDisplaySrc,
  toUniteImageFileName,
  toUniteListImageSrcFromApi,
} from "../../utils/uniteImageBlob";
import './gestion-unites.css';

type UniteListImageCellProps = {
  idUnite: Guid;
  liveImageSrc?: string;
  resolveImageSrc: (idUnite: Guid) => Promise<string>;
};

function UniteListImageCell({ idUnite, liveImageSrc, resolveImageSrc }: UniteListImageCellProps) {
  const [imageSrc, setImageSrc] = useState(liveImageSrc || '');

  useEffect(() => {
    if (liveImageSrc) {
      setImageSrc(liveImageSrc);
      return;
    }

    let cancelled = false;
    resolveImageSrc(idUnite).then((src) => {
      if (!cancelled) {
        setImageSrc(src);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [idUnite, liveImageSrc, resolveImageSrc]);

  if (!imageSrc) {
    return null;
  }

  return <img src={imageSrc} alt="" className="gestion-unites-liste-image" />;
}

type UniteIconeSelectItem = {
  text: string;
  value: string;
  imageSrc: string;
};

export default function GestionUnites() {
  const activiteDataSourceOption: IFormBaseDataSourceOptions<IGestionUnite> = useMemo(() => ({
    path: "unite",
    customReadByIdPath: "unite/{key}/gestion",
    customSauvegarderPath: "unite/save",
    primaryKey: "idUnite",
  }), []);
  const uniteFormDataSource = useCustomDataSource<IUnite, IGestionUnite>(activiteDataSourceOption);
  const {
    setMainForm,
    selectedDataRow: selectedUnite,
    mode, setMode,
    isFieldModifier,
    selectedId,
    setSelectedDataRow,
    setIsFieldModifier,
    onFieldDataChanged
  } = uniteFormDataSource;

  const { fetchData } = useApi();
  const uniteListImageCacheRef = useRef<Map<string, string>>(new Map());

  //constants
  const timeMask = "NN:NN";
  const timeRules = { "N": /[0-9]/ };
  const formRef = useRef<FormRef>(null);
  const uniteImageFileUploaderRef = useRef<any>(null);
  const [selectedUniteImageId, setSelectedUniteImageId] = useState<Guid | undefined>();
  const [editingUniteServiceRow, setEditingUniteServiceRow] = useState<IUniteService | null>(null);
  const [editingUniteEquipmentRow, setEditingUniteEquipmentRow] = useState<IUniteEquipment | null>(null);
  const [editingUniteForfaitRow, setEditingUniteForfaitRow] = useState<IUniteForfait | null>(null);
  const [editingUniteTrancheAgeRow, setEditingUniteTrancheAgeRow] = useState<IUnitePersonneTrancheAge | null>(null);
  const { editorOptions: typesHebergementEditorOptions } = useEnum(undefined, "gestionTypesHebergement", '50%', 3);
  const { editorOptions: typesEnsoleillementEditorOptions } = useEnum(undefined, "gestionTypesEnsoleillement", '50%');

  const { nuiteesEditorOptions, descriptionOptions, codeUniteOptions, dimensionTerrainOptions, actifOptions, fumeurOptions, interdireAnimauxOptions, inclureRapportOptions, dateDernierMenageOptions, entreeSortieOptions, disponibleEnLigneOptions, afficherCalendrierOptions, photo1Options, photo2Options, photo3Options, photo4Options, etageOptions, spinOptions, spinOptions2, heuresOptions, descriptionWebOptions, anneeOptions } = useMemo(() => {
    return {
      nuiteesEditorOptions: {
        min: 0,
        max: 32,
      },
      descriptionOptions: { maxLength: 40 },
      codeUniteOptions: { maxLength: 12, width: "55%" },
      dimensionTerrainOptions: { maxLength: 15 },
      actifOptions: { text: "Actif" },
      fumeurOptions: { text: "Fumeur" },
      interdireAnimauxOptions: { text: "Interdire les animaux" },
      inclureRapportOptions: { text: "Inclure dans le rapport de ménage" },
      dateDernierMenageOptions: { name: "dateDernierMenage", width: "35%" },
      entreeSortieOptions: { text: "Entrée/Sortie (Pull through)" },
      disponibleEnLigneOptions: { text: "Disponible pour les réservations en ligne" },
      afficherCalendrierOptions: { text: "Permettre d'afficher les calendriés des disponibilités" },
      photo1Options: { width: "150%", text: "Ajouter" },
      photo2Options: { width: "150%", text: "Supprimer" },
      photo3Options: { width: "50%", text: "↑" },
      photo4Options: { width: "50%", text: "↓" },
      etageOptions: { min: 0, max: 120, showSpinButtons: true },
      spinOptions: { min: 0, max: 999, showSpinButtons: true },
      spinOptions2: { min: 0, max: 999, showSpinButtons: true, width: "20%" },
      heuresOptions: { mask: timeMask, maskRules: timeRules },
      descriptionWebOptions: { height: 150, maxLength: 3000 },
      anneeOptions: {
        defaultValue: new Date().getFullYear(),
        min: 1970,
        max: new Date().getFullYear(),
        showSpinButtons: true
      },
    };
  }, []);

  const isShortUniteIconPath = useCallback((image?: string) => {
    return !!image && !image.startsWith('data:') && image.length <= 50;
  }, []);

  const activeUniteImages = useMemo(() => {
    return (selectedUnite?.uniteImages || [])
      .filter((img) => !img.isDeleted)
      .sort((a, b) => a.priorite - b.priorite);
  }, [selectedUnite?.uniteImages]);

  const uniteIconesSelectItems = useMemo((): UniteIconeSelectItem[] => {
    return activeUniteImages
      .filter((img) => img.idUniteImage)
      .map((img, index) => {
        const fileName = toUniteImageFileName(img.image);
        const label = fileName || (index === 0 ? 'Icône principale' : `Icône ${index + 1}`);
        return {
          text: index === 0 ? `${label} (utilisée)` : label,
          value: img.idUniteImage!.toString(),
          imageSrc: toUniteImageDisplaySrc(img.image),
        };
      });
  }, [activeUniteImages]);

  const selectedPrincipalIconeSrc = useMemo(() => {
    const principal = activeUniteImages[0];
    return principal?.image ? toUniteImageDisplaySrc(principal.image) : '';
  }, [activeUniteImages]);

  const selectedUniteIconePreviewSrc = useMemo(() => {
    if (!selectedUniteImageId) {
      return '';
    }
    const item = uniteIconesSelectItems.find((i) => i.value === selectedUniteImageId.toString());
    return item?.imageSrc ?? '';
  }, [selectedUniteImageId, uniteIconesSelectItems]);

  const iconePreviewSrc = useMemo(() => {
    return selectedUniteIconePreviewSrc || selectedPrincipalIconeSrc;
  }, [selectedUniteIconePreviewSrc, selectedPrincipalIconeSrc]);

  useEffect(() => {
    if (!selectedUnite?.idUnite || !selectedPrincipalIconeSrc) {
      return;
    }
    uniteListImageCacheRef.current.set(selectedUnite.idUnite.toString(), selectedPrincipalIconeSrc);
  }, [selectedUnite?.idUnite, selectedPrincipalIconeSrc]);

  const resolveUniteListImageSrc = useCallback(async (idUnite: Guid) => {
    const cacheKey = idUnite.toString();
    const cached = uniteListImageCacheRef.current.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const data = await fetchData('get', {
        customPath: `unite/${idUnite.toString()}/Image`,
        returnOnly: true,
      });
      const src = toUniteListImageSrcFromApi(data);
      if (src) {
        uniteListImageCacheRef.current.set(cacheKey, src);
      }
      return src;
    } catch {
      return '';
    }
  }, [fetchData]);

  const uniteIconeItemRender = useCallback((item: UniteIconeSelectItem) => {
    if (!item) {
      return null;
    }
    return (
      <div className="gestion-unites-icone-select-item">
        {item.imageSrc ? (
          <img src={item.imageSrc} alt="" className="gestion-unites-icone-select-item-img" />
        ) : null}
        <span>{item.text}</span>
      </div>
    );
  }, []);

  const updateUniteImages = useCallback((uniteImages: IUniteImage[]) => {
    setSelectedDataRow((prev) => {
      if (!prev) {
        return prev;
      }
      const image = isShortUniteIconPath(prev.image) ? prev.image : '';
      return { ...prev, image, uniteImages };
    });
    if (mode !== Mode.Lecture) {
      setIsFieldModifier(true);
    }
  }, [setSelectedDataRow, setIsFieldModifier, mode, isShortUniteIconPath]);

  const onUniteImageUploaded = useCallback((e: { value?: File[] }) => {
    const file = e.value?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      const nextPriorite = activeUniteImages.length > 0
        ? Math.max(...activeUniteImages.map((img) => img.priorite)) + 1
        : 0;
      const newImage: IUniteImage = {
        idUniteImage: new Guid(),
        idUnite: selectedUnite?.idUnite || Guid.empty,
        image: packUniteImageWithFileName(file.name, base64),
        priorite: nextPriorite,
        isDeleted: false,
        dateCreation: new Date(),
        dateModification: new Date(),
        idUsagerCreation: Guid.empty,
        idUsagerModification: Guid.empty,
      };

      updateUniteImages([...(selectedUnite?.uniteImages || []), newImage]);
      setSelectedUniteImageId(newImage.idUniteImage);
      uniteImageFileUploaderRef.current?.instance?.reset();
    };
    reader.readAsDataURL(file);
  }, [activeUniteImages, selectedUnite?.idUnite, selectedUnite?.uniteImages, updateUniteImages]);

  const deleteSelectedUniteImage = useCallback(() => {
    const targetId = selectedUniteImageId
      ?? activeUniteImages[activeUniteImages.length - 1]?.idUniteImage;
    if (!targetId) {
      return;
    }

    const uniteImages = (selectedUnite?.uniteImages || [])
      .map((img) => {
        if (!img.idUniteImage?.equals(targetId)) {
          return img;
        }
        return img.idUniteImage && !Guid.IsGuidTemporaire(img.idUniteImage)
          ? { ...img, isDeleted: true }
          : null;
      })
      .filter((img): img is IUniteImage => img !== null);

    updateUniteImages(uniteImages);
    setSelectedUniteImageId(undefined);
  }, [selectedUniteImageId, activeUniteImages, selectedUnite?.uniteImages, updateUniteImages]);

  const setPrincipalUniteImage = useCallback((idUniteImage: Guid) => {
    const selected = activeUniteImages.find((img) => img.idUniteImage?.equals(idUniteImage));
    if (!selected) {
      return;
    }

    const others = activeUniteImages.filter((img) => !img.idUniteImage?.equals(idUniteImage));
    const reorderedWithPriorite = [selected, ...others].map((img, index) => ({ ...img, priorite: index }));
    const deletedImages = (selectedUnite?.uniteImages || []).filter((img) => img.isDeleted);

    updateUniteImages([...reorderedWithPriorite, ...deletedImages]);
    setSelectedUniteImageId(idUniteImage);
  }, [activeUniteImages, selectedUnite?.uniteImages, updateUniteImages]);

  const { couleurOptions: couleurTexteColorBoxOptions } = useCouleurInput<IGestionUnite>({ champ: 'couleurTexte', dataRow: selectedUnite, setDataRow: setSelectedDataRow, setIsFieldModifier, isModeModifier: mode !== Mode.Lecture, });
  const { couleurOptions: couleurBackgroundColorBoxOptions } = useCouleurInput<IGestionUnite>({ champ: 'couleurBackground', dataRow: selectedUnite, setDataRow: setSelectedDataRow, setIsFieldModifier, isModeModifier: mode !== Mode.Lecture, });

  const onUniteFormFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    if (e.dataField === 'couleurTexte' || e.dataField === 'couleurBackground') {
      return;
    }
    onFieldDataChanged(e);
  }, [onFieldDataChanged]);

  const { selectBox: planBudgetNuitSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "planentete", showClear: mode !== Mode.Lecture });
  const { selectBox: planBudgetRevenuSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "planentete", showClear: mode !== Mode.Lecture });
  const { selectBox: typeBudgetNuitSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "plandetailtype", showClear: mode !== Mode.Lecture });
  const { selectBox: typeBudgetRevenuSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "plandetailtype", showClear: mode !== Mode.Lecture });
  const { selectBox: typeChampSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'OptionReservationTypeChamp', showClear: mode !== Mode.Lecture });
  const { selectBox: modeCalculSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'OptionReservationModeCalcul', showClear: mode !== Mode.Lecture });
  const { selectBox: typeOptionSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: 'OptionReservationTypeAuxiliaire', showClear: mode !== Mode.Lecture });
  const { editorOptions: typesUnitesEditorOption, PopupGestion: PopupGestionTypesUnites } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "typeunite",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idTypeUnite", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });
  const { editorOptions: planParDefautEditorOption, PopupGestion: PopupGestionPlan } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "planentete",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idPlanEntete", Guid.empty.toString());
    },
    titre: "Gestionnaire des plans",
    // cacheKey: "planentete-defaut",
    showClear: mode !== Mode.Lecture
  });
  const { editorOptions: folioEditorOption, PopupGestion: PopupGestionFolios } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "folio",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idFolio", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });
  const { editorOptions: categorieUniteEditorOption, PopupGestion: PopupGestionCategorieUnite } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "unitecategorie",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idUniteCategorie", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });
  const { editorOptions: pointEauEditorOption, PopupGestion: PopupGestionPointEau } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "pointeau",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idPointEau", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });
  const { editorOptions: secteurEditorOption, PopupGestion: PopupGestionSecteur } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "secteur",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idSecteur", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });
  // const { selectBox: planSelectBox } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "planentete", showClear: mode !== Mode.Lecture });
  const { editorOptions: produitPourOptionEditorOption, PopupGestion: PopupGestionCatalogue } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "catalogue",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idProduitOption", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });
  // const { editorOptions: produitsEditorOption, PopupGestion: PopupGestionProduits } = useLookUpBase({ isModeModifier:mode !== Mode.Lecture, controllerName: "produitoption", setDataRow: () => setDataRow("idProduit"), showClear:mode !== Mode.Lecture });
  const { editorOptions: uniteServiceEditorOptions, PopupGestion: PopupGestionUniteServiceType } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "uniteservicetype",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des types de services",
  });
  const { editorOptions: uniteEquipementEditorOptions, PopupGestion: PopupGestionUniteEquipmentType } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "uniteequipmenttype",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des types d'équipements",
  });

  const uniteServiceGridLookupOptions = useMemo(() => {
    const { onValueChanged: _, ...lookupOptions } = uniteServiceEditorOptions;
    return lookupOptions;
  }, [uniteServiceEditorOptions]);

  const uniteEquipementGridLookupOptions = useMemo(() => {
    const { onValueChanged: _, ...lookupOptions } = uniteEquipementEditorOptions;
    return lookupOptions;
  }, [uniteEquipementEditorOptions]);

  const planParDefautGridLookupOptions = useMemo(() => {
    const { onValueChanged: _, ...lookupOptions } = planParDefautEditorOption;
    return lookupOptions;
  }, [planParDefautEditorOption]);

  const { editorOptions: trancheAgeEditorOptions, PopupGestion: PopupGestionTrancheAge } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "trancheAge",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des tranches d'âges",
  });

  const trancheAgeGridLookupOptions = useMemo(() => {
    const { onValueChanged: _, ...lookupOptions } = trancheAgeEditorOptions;
    return lookupOptions;
  }, [trancheAgeEditorOptions]);

  const { editorOptions: messagesEditorOption, PopupGestion: PopupGestionMessages } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "message",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idMessage", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });
  const { dateOptions: dateTimeOptions } = useDateInput(true);
  const { reservationCouleurGridCellItemRender } = useReservationCouleur();
  const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);

  const setDataRow = (e: string) => {
    setSelectedDataRow((prev: IGestionUnite) => (
      { ...prev, [e]: Guid.empty.toString() }
    ));
    setIsFieldModifier(true);
  }
  const onSelectionchanged = (item: any, e: string) => {
    setIsFieldModifier(true);
    setSelectedDataRow((prev: IGestionUnite) => {
      return { ...prev, [e]: item.id }
    })
  }

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (!selectedUnite?.image || isShortUniteIconPath(selectedUnite.image)) {
      return;
    }
    setSelectedDataRow((prev) => (prev ? { ...prev, image: '' } : prev));
  }, [selectedId, selectedUnite?.image, setSelectedDataRow, isShortUniteIconPath]);

  useEffect(() => {
    setSelectedUniteImageId(activeUniteImages[0]?.idUniteImage);
  }, [selectedId, activeUniteImages]);

  useEffect(() => {
    // if (selectedId) _loadDataUnite(selectedId);
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [selectedId, mode])

  //function de gestion de chaque lignes de l'onglet "option"
  const logEvent = (eventName: string) => {
    // setEvents((prevEvents) => [eventName, ...prevEvents]);
  };

  const renderUniteImageCell = useCallback((cellData: { data?: IUnite }) => {
    const row = cellData.data;
    const idUnite = row?.idUnite;
    if (!idUnite || idUnite.equals(Guid.empty)) {
      return null;
    }

    const liveImageSrc = selectedUnite?.idUnite?.equals(idUnite)
      ? selectedPrincipalIconeSrc
      : undefined;

    return (
      <UniteListImageCell
        idUnite={idUnite}
        liveImageSrc={liveImageSrc}
        resolveImageSrc={resolveUniteListImageSrc}
      />
    );
  }, [selectedUnite?.idUnite, selectedPrincipalIconeSrc, resolveUniteListImageSrc]);

  const renderUniteCouleurCell = useCallback((field: 'couleurTexte' | 'couleurBackground') => (cellData: { data?: Record<string, unknown> }) => {
    const pascalField = `${field.charAt(0).toUpperCase()}${field.slice(1)}`;
    const raw = cellData.data?.[field] ?? cellData.data?.[pascalField];
    const hex = uniteColorFieldToColorBoxValue(raw);
    return (
      <span
        className={`gestion-unites-liste-couleur${hex ? '' : ' gestion-unites-liste-couleur--empty'}`}
        style={hex ? { backgroundColor: hex } : undefined}
        title={hex || undefined}
      />
    );
  }, []);

  const syncUniteGridRows = useCallback((
    component: { option?: (name: string) => unknown },
    field: 'services' | 'equipements' | 'forfaits' | 'tranchesAge',
  ) => {
    const items = component?.option?.('dataSource');
    if (!Array.isArray(items)) {
      return;
    }
    setSelectedDataRow((prev) => (prev ? { ...prev, [field]: [...items] } : prev));
    setIsFieldModifier(true);
  }, [setSelectedDataRow, setIsFieldModifier]);


  return (
    <FormListBase data={uniteFormDataSource}>
      <FormListBaseGrid dataSource={uniteFormDataSource.dataSource}
        onFocusedRowChanged={() => {
          if (mode === Mode.Modifier) setMode(Mode.Lecture)
        }}>
        <Column key={"grid-col-image"} dataType={"object"} caption="" allowHiding={false} width={100} alignment="center" allowSorting={false} allowFiltering={false} cellRender={renderUniteImageCell} />
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-type-unite"} editorOptions={typesUnitesEditorOption} dataField={"idTypeUnite"} caption="Type d'unité" allowHiding={false}>
          <Lookup {...typesUnitesEditorOption} />
        </Column>
        <Column key={"grid-col-type-idPlanEntete"} dataField={"idPlanEntete"} caption="Plan par défaut" allowHiding={false}>
          <Lookup {...planParDefautEditorOption} />
        </Column>
        <Column key={"grid-col-type-couleurTexte"} dataType={"object"} caption="Couleur de texte" allowHiding={false} width={56} alignment="center" allowSorting={false} allowFiltering={false} cellRender={renderUniteCouleurCell('couleurTexte')} />
        <Column key={"grid-col-type-couleurBackground"} dataType={"object"} caption="Couleur d'arrière plan" allowHiding={false} width={56} alignment="center" allowSorting={false} allowFiltering={false} cellRender={renderUniteCouleurCell('couleurBackground')} />

      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedUnite?.codeUnite || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      <FormListBaseTab>
        <PopupGestionCatalogue
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idProduitOption", data.idProduit);
          }}
        >
          <GestionCatalogueProduits isModeSelection={true} />
        </PopupGestionCatalogue>
        <PopupGestionTypesUnites
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idTypeUnite", data.idTypeUnite);
          }}
        >
          <GestionTypesUnites isModeSelection={true} />
        </PopupGestionTypesUnites>
        <PopupGestionPlan
          onSelectionchanged={(data: any) => {
            const idPlanEntete = data?.idPlanEntete || data?.id;
            if (!idPlanEntete) {
              return;
            }
            if (editingUniteForfaitRow && selectedUnite) {
              const updatedForfaits = selectedUnite.forfaits.map((item) => {
                if (item === editingUniteForfaitRow
                  || (item.idUniteForfait && editingUniteForfaitRow.idUniteForfait?.equals(item.idUniteForfait))) {
                  return { ...item, idPlanEntete: new Guid(String(idPlanEntete)) };
                }
                return item;
              });
              setSelectedDataRow((prev) => (prev ? { ...prev, forfaits: updatedForfaits } : prev));
              setIsFieldModifier(true);
              return;
            }
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idPlanEntete", idPlanEntete);
          }}
        >
          <GestionPlans isModeSelection={true} />
        </PopupGestionPlan>
        <PopupGestionFolios
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idFolio", data.idFolio);
          }}
        >
          <GestionFolios isModeSelection={true} />
        </PopupGestionFolios>
        <PopupGestionCategorieUnite
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idUniteCategorie", data.idUniteCategorie);
          }}
        >
          <GestionCategoriesUnites isModeSelection={true} />
        </PopupGestionCategorieUnite>
        <PopupGestionSecteur
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idSecteur", data.idSecteur);
          }}
        >
          <GestionSecteurs isModeSelection={true} />
        </PopupGestionSecteur>
        <PopupGestionPointEau
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idPointEau", data.idPointEau);
          }}
        >
          <GestionPointsEau isModeSelection={true} />
        </PopupGestionPointEau>
        <PopupGestionMessages
          onSelectionchanged={(data: any) => {
            const selectedMessageId = data?.idMessage || data?.id || data;
            if (!selectedMessageId) return;
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idMessage", selectedMessageId);
          }}
        >
          <GestionMessages isModeSelection={true} />
        </PopupGestionMessages>
        <PopupGestionUniteServiceType
          onSelectionchanged={(data: any) => {
            const idUniteServiceType = data?.idUniteServiceType || data?.id;
            if (!idUniteServiceType || !editingUniteServiceRow || !selectedUnite) {
              return;
            }
            const updatedServices = selectedUnite.services.map((item) => {
              if (item === editingUniteServiceRow
                || (item.idUniteService && editingUniteServiceRow.idUniteService?.equals(item.idUniteService))) {
                return { ...item, idUniteServiceType: new Guid(String(idUniteServiceType)) };
              }
              return item;
            });
            setSelectedDataRow((prev) => ({ ...prev, services: updatedServices }));
            setIsFieldModifier(true);
          }}
        >
          <GestionTypesServices isModeSelection={true} />
        </PopupGestionUniteServiceType>
        <PopupGestionUniteEquipmentType
          onSelectionchanged={(data: any) => {
            const idUniteEquipmentType = data?.idUniteEquipmentType || data?.id;
            if (!idUniteEquipmentType || !editingUniteEquipmentRow || !selectedUnite) {
              return;
            }
            const updatedEquipements = selectedUnite.equipements.map((item) => {
              if (item === editingUniteEquipmentRow
                || (item.idUniteEquipment && editingUniteEquipmentRow.idUniteEquipment?.equals(item.idUniteEquipment))) {
                return { ...item, idUniteEquipmentType: new Guid(String(idUniteEquipmentType)) };
              }
              return item;
            });
            setSelectedDataRow((prev) => ({ ...prev, equipements: updatedEquipements }));
            setIsFieldModifier(true);
          }}
        >
          <GestionTypesEquipements isModeSelection={true} />
        </PopupGestionUniteEquipmentType>
        <PopupGestionTrancheAge
          onSelectionchanged={(data: any) => {
            const idTrancheAge = data?.idTrancheAge || data?.id;
            if (!idTrancheAge || !editingUniteTrancheAgeRow || !selectedUnite) {
              return;
            }
            const newIdTrancheAge = new Guid(String(idTrancheAge));
            const updatedTranchesAge = selectedUnite.tranchesAge.map((item) => {
              if (item === editingUniteTrancheAgeRow
                || (item.idUnitePersonneTrancheAge && editingUniteTrancheAgeRow.idUnitePersonneTrancheAge?.equals(item.idUnitePersonneTrancheAge))) {
                return { ...item, idTrancheAge: newIdTrancheAge };
              }
              return item;
            });
            setSelectedDataRow((prev) => (prev ? { ...prev, tranchesAge: updatedTranchesAge } : prev));
            setIsFieldModifier(true);
          }}
        >
          <ConfigurationTranchesAges isModeSelection={true} />
        </PopupGestionTrancheAge>

        <TabItem title="Informations">
          <div style={{ height: "100%", paddingBottom: "1rem" }}>
            {/* <div style={{ width: '50%' }}> */}
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={false}
              formData={selectedUnite || {} as IGestionUnite}
              className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onUniteFormFieldDataChanged} >
              <GroupItem alignItemLabels={true} cssClass="gestion-unites-champs-informations">
                <SimpleItem key={"frm-description"} dataField={"description"}
                  editorOptions={descriptionOptions} />
                <SimpleItem key={"frm-description-client"} dataField={"descriptionClient"} editorOptions={descriptionOptions}>
                  <Label text={"Description client"} />
                </SimpleItem>
                <SimpleItem key={"frm-description-anglaise"} dataField={"descriptionAnglaise"} editorOptions={descriptionOptions}>
                  <Label text={"Description anglaise"} />
                </SimpleItem>
                <SimpleItem key={"frm-code-unite"} dataField={"codeUnite"} editorOptions={codeUniteOptions}>
                  <Label text={"Code"} />
                </SimpleItem>
                <SimpleItem key={"frm-type-unite"}
                  dataField={"idTypeUnite"}
                  editorType={"dxSelectBox"}
                  editorOptions={typesUnitesEditorOption}
                >
                  <Label text={"Type d'unité"} />
                </SimpleItem>
                <SimpleItem
                  key={"frm-id-plan-entete"}
                  dataField={"idPlanEntete"}
                  editorType={"dxSelectBox"}
                  editorOptions={planParDefautEditorOption}
                >
                  <Label text={"Plan par défaut"} />
                </SimpleItem>
                <SimpleItem key={"frm-id-produits-options"}
                  dataField={"idProduitOption"}
                  editorType={"dxSelectBox"}
                  editorOptions={produitPourOptionEditorOption}
                >
                  <Label text={"Produits pour les options"} />
                </SimpleItem>
                <SimpleItem key={"frm-id-message"}
                  dataField={"idMessage"}
                  editorType={"dxSelectBox"}
                  editorOptions={messagesEditorOption}
                >
                  <Label text={"Message par défaut"} />
                </SimpleItem>
                <SimpleItem
                  key={"frm-id-folio"}
                  dataField={"idFolio"}
                  editorType={"dxSelectBox"}
                  editorOptions={folioEditorOption}
                >
                  <Label text={"Folio"} />
                </SimpleItem>
                <SimpleItem key={"frm-id-categorie-unite"}
                  dataField={"idUniteCategorie"}
                  editorType={"dxSelectBox"}
                  editorOptions={categorieUniteEditorOption}
                >
                  <Label text={"Catégorie d'unité"} />
                </SimpleItem>
                <SimpleItem
                  key={"frm-id-secteur"}
                  dataField={"idSecteur"}
                  editorType={"dxSelectBox"}
                  editorOptions={secteurEditorOption}
                >
                  <Label text={"Secteur"} />
                </SimpleItem>
                <SimpleItem
                  key={"frm-id-point-eau"}
                  dataField={"idPointEau"}
                  editorType={"dxSelectBox"}
                  editorOptions={pointEauEditorOption}
                >
                  <Label text={"Point d'eau"} />
                </SimpleItem>
                <SimpleItem key={"frm-type-hebergement"} dataField={"typeHebergement"} editorType={"dxSelectBox"} editorOptions={typesHebergementEditorOptions}>
                  <Label text={"Type d'hébergement"} />
                </SimpleItem>
                <SimpleItem key={"frm-ensoleillement"} dataField={"ensoleillement"} editorType={"dxSelectBox"} editorOptions={typesEnsoleillementEditorOptions}>
                  <Label text={"Ensoleillement"} />
                </SimpleItem>
                <SimpleItem key={"frm-dimension-terrain"} dataField={"dimensionTerrain"} editorOptions={dimensionTerrainOptions}>
                  <Label text={"Dimensions du terrain (long × large)"} />
                </SimpleItem>
              </GroupItem>

              <GroupItem caption={"Personnes additionnelles (Ces informations ont la priorité sur celles du plan)"}>
                <SimpleItem key={"frm-nb-personnes-avant-plan-pers-add"} dataField={"nbPersonnesAvantPlanPersAdd"} editorType={"dxNumberBox"} editorOptions={spinOptions2}>
                  <Label text={"Nombre de personnes avant de facturer des personnes additionnelles"} />
                </SimpleItem>
                <SimpleItem key={"frm-nb-adultes-avant-plan-add"} dataField={"nbAdultesAvantPlanAdd"} editorType={"dxNumberBox"} editorOptions={spinOptions2}>
                  <Label text={"Nombre d'adultes avant de facturer des personnes additionnels"} />
                </SimpleItem>
                <SimpleItem key={"frm-nb-enfants-avant-plan-add"} dataField={"nbEnfantsAvantPlanAdd"} editorType={"dxNumberBox"} editorOptions={spinOptions2}>
                  <Label text={"Nombre d'enfants avant de facturer des enfants additionnels"} />
                </SimpleItem>
              </GroupItem>

              <GroupItem caption={"Options"} colCount={2} alignItemLabels={false}>
                <GroupItem colSpan={1}>
                  <SimpleItem key={"frm-etage"} dataField={"etage"} editorType={"dxNumberBox"} editorOptions={etageOptions}>
                    <Label text={"Étage"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-personne-minimum"} dataField={"nombrePersonneMinimum"} editorType={"dxNumberBox"} editorOptions={spinOptions}>
                    <Label text={"Nb. de personnes minimum"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-personne"} dataField={"nombrePersonne"} editorType={"dxNumberBox"} editorOptions={spinOptions}>
                    <Label text={"Nb. de personnes maximum"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-adultes-minimum"} dataField={"nombreAdultesMinimum"} editorType={"dxNumberBox"} editorOptions={spinOptions}>
                    <Label text={"Nb. d'adultes minimum (en ligne seulement)"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-adultes-maximum"} dataField={"nombreAdultesMaximum"} editorType={"dxNumberBox"} editorOptions={spinOptions}>
                    <Label text={"Nb. d'adultes maximum"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-enfants-maximum"} dataField={"nombreEnfantsMaximum"} editorType={"dxNumberBox"} editorOptions={spinOptions}>
                    <Label text={"Nb. d'enfants maximum"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-heure-arrivee"} dataField={"heureArrivee"} editorOptions={heuresOptions}>
                    <Label text={"Heure d'arrivée"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-heure-depart"} dataField={"heureDepart"} editorOptions={heuresOptions}>
                    <Label text={"Heure de départ"} />
                  </SimpleItem>
                </GroupItem>
                <GroupItem colSpan={1}>
                  <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"} editorOptions={actifOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-fumeur"} dataField={"fumeur"} editorType={"dxCheckBox"} editorOptions={fumeurOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-interdire-animaux"} dataField={"interdireAnimaux"} editorType={"dxCheckBox"} editorOptions={interdireAnimauxOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-inclure-rapport-menage"} dataField={"inclureRapportMenage"} editorType={"dxCheckBox"} editorOptions={inclureRapportOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-date-dernier-menage"} dataField={"dateDernierMenage"} editorType={"dxDateBox"} editorOptions={dateDernierMenageOptions}>
                    <Label text={"Date du dernier ménage"} />
                  </SimpleItem>
                </GroupItem>
              </GroupItem>
              <GroupItem cssClass="gestion-unites-apparence" alignItemLabels={false}>
                <GroupItem caption={"Apparence"}>
                  <SimpleItem colSpan={2}>
                    <Label text={"Icônes"} />
                    <div className="icone-select-container">
                      {iconePreviewSrc && (
                        <img
                          src={iconePreviewSrc}
                          alt=""
                          className="gestion-unites-icone-select-item-img"
                        />
                      )}
                      <SelectBox
                        dataSource={uniteIconesSelectItems}
                        value={selectedUniteImageId?.toString() ?? null}
                        displayExpr="text"
                        valueExpr="value"
                        disabled={mode === Mode.Lecture}
                        placeholder="Sélectionner une icône"
                        searchEnabled={uniteIconesSelectItems.length > 5}
                        itemRender={uniteIconeItemRender}
                        dropDownOptions={{ minWidth: 280 }}
                        onValueChanged={(e) => {
                          if (!e.value) {
                            setSelectedUniteImageId(undefined);
                            return;
                          }
                          if (mode === Mode.Lecture) {
                            setSelectedUniteImageId(new Guid(String(e.value)));
                            return;
                          }
                          setPrincipalUniteImage(new Guid(String(e.value)));
                        }}
                        stylingMode="filled"
                        showClearButton={false}
                      />
                      <FileUploader
                        ref={uniteImageFileUploaderRef}
                        disabled={mode === Mode.Lecture}
                        accept="image/*"
                        uploadMode="useForm"
                        showFileList={false}
                        selectButtonText="+"
                        labelText=""
                        onValueChanged={onUniteImageUploaded}
                      />
                      <Button
                        disabled={mode === Mode.Lecture || activeUniteImages.length === 0}
                        onClick={deleteSelectedUniteImage}
                        text="X"
                      />
                    </div>
                  </SimpleItem>
                  <SimpleItem key={"frm-couleur-texte"}>
                    <Label text={"Couleur de texte"} />
                    <ColorBox {...couleurTexteColorBoxOptions} />
                  </SimpleItem>
                  <SimpleItem key={"frm-couleur-background"}>
                    <Label text={"Couleur de l'arrière-plan"} />
                    <ColorBox {...couleurBackgroundColorBoxOptions} />
                  </SimpleItem>
                </GroupItem>
              </GroupItem>
            </Form>
            {/* </div> */}
          </div>
        </TabItem>
        <TabItem title="Hors-Service">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedUnite || {} as IUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem>
              <DataGrid
                // keyExpr="idUniteHorsService"
                showBorders={true}
                allowColumnResizing={true}
                dataSource={selectedUnite.uniteHorsServices}
                allowColumnReordering={false}
                filterValue={dataGridFilterValue}
                height={"100%"}
                noDataText={"Aucune unité hors service"}
                onInitNewRow={(e: any) => {
                  e.data.idUnite = selectedUnite.idUnite;
                  e.data.isDeleted = false;
                }}
                onEditorPreparing={(e: any) => {
                  if (e.dataField === "idUniteHorsService") {
                    const originalOnValueChanged = e.editorOptions.onValueChanged;
                    e.editorOptions.onValueChanged = (args: any) => {
                      if (originalOnValueChanged) {
                        originalOnValueChanged(args);
                      }

                      const currentData = e.component.option("dataSource");
                      const rowIndex = e.rowIndex;
                      const rowData = currentData[rowIndex];

                      const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                      setSelectedDataRow(prev => ({
                        ...prev,
                        uniteHorsServices: updatedData
                      }));
                      setIsFieldModifier(true);
                    }
                  };
                }}
                onRowRemoving={async (e) => {
                  e.cancel = true;
                  // if (e?.data?.idPermisType) {
                  const index = selectedUnite.uniteHorsServices.findIndex(item =>
                    item.idUniteHorsService?.equals(e.data.idUniteHorsService)
                  );
                  if (index !== -1) {
                    const updatedData = [...selectedUnite.uniteHorsServices];
                    updatedData[index] = { ...updatedData[index], isDeleted: true };
                    setSelectedDataRow(prev => ({
                      ...prev,
                      uniteHorsServices: updatedData
                    }));
                  }
                  // }
                }}
              >
                <Editing
                  mode="cell"
                  allowUpdating={mode !== Mode.Lecture}
                  allowDeleting={mode !== Mode.Lecture}
                  allowAdding={mode !== Mode.Lecture}
                  newRowPosition={"first"}
                  startEditAction={"click"}
                />
                <Column dataField="dateDebut" caption={"Date de début"} dataType={"date"} format={"yyyy-MM-dd"} />
                <Column dataField="dateFin" caption={"Date de fin"} dataType={"date"} format={"yyyy-MM-dd"} />
                <Column dataField="description" caption={"Description"} dataType={"string"} />
                <Column dataField="isDeleted" dataType="boolean" visible={false} />
              </DataGrid>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Options">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedUnite || {} as IUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem>
              <DataGrid
                showBorders={true}
                allowColumnResizing={true}
                dataSource={selectedUnite.optionReservations}
                allowColumnReordering={false}
                filterValue={dataGridFilterValue}
                height={"100%"}
                noDataText={"Aucune option"}
                onInitNewRow={(e: any) => {
                  e.data.idUnite = selectedUnite.idUnite;
                  e.data.isDeleted = false;
                }}
                onEditorPreparing={(e: any) => {
                  const originalOnValueChanged = e.editorOptions.onValueChanged;
                  e.editorOptions.onValueChanged = (args: any) => {
                    if (originalOnValueChanged) {
                      originalOnValueChanged(args);
                    }

                    const currentData = e.component.option("dataSource");
                    const rowIndex = e.rowIndex;
                    const rowData = currentData[rowIndex];

                    const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                    setSelectedDataRow(prev => ({
                      ...prev,
                      optionReservations: updatedData
                    }));
                    setIsFieldModifier(true);
                  }
                }}
                onRowRemoving={async (e) => {
                  e.cancel = true;
                  // if (e?.data?.idPermisType) {
                  const index = selectedUnite.optionReservations.findIndex(item =>
                    item.idOptionReservation?.equals(e.data.idOptionReservation)
                  );
                  if (index !== -1) {
                    const updatedData = [...selectedUnite.optionReservations];
                    updatedData[index] = { ...updatedData[index], isDeleted: true };
                    setSelectedDataRow(prev => ({
                      ...prev,
                      optionReservations: updatedData
                    }));
                  }
                  // }
                }}
              // ------- use use this methode when an events on row is called ---
              // onSaved={() => logEvent('Saved')}
              // onSaving={() => logEvent('Saving')}
              // onInitNewRow={() => logEvent('InitNewRow')}
              // onRowRemoved={() => logEvent('RowRemoved')}
              // onRowUpdated={() => logEvent('RowUpdated')}
              // onRowInserted={() => logEvent('RowInserted')}
              // onRowUpdating={() => logEvent('RowUpdating')}
              // onRowRemoving={() => logEvent('RowRemoving')}
              // onEditingStart={() => logEvent('EditingStart')}
              // onRowInserting={() => logEvent('RowInserting')}
              // onEditCanceled={() => logEvent('EditCanceled')}
              // onEditCanceling={() => logEvent('EditCanceling')}
              >
                <Editing
                  mode="cell"
                  allowUpdating={mode !== Mode.Lecture}
                  allowDeleting={mode !== Mode.Lecture}
                  allowAdding={mode !== Mode.Lecture}
                  newRowPosition={"first"}
                  startEditAction={"click"}
                />
                <Column dataField="description" caption="Description" dataType="string"></Column>
                <Column dataField="idOptionReservation" caption="Type d'options" cellRender={typeOptionSelectBox} />
                <Column dataField="elmentArray" caption="Element" dataType="number" alignment={'left'} />
                <Column dataField="modeCalcul" caption="Mode de calcul" cellRender={modeCalculSelectBox} />
                <Column dataField="typeChamp" caption="Type de champ" dataType="string" cellRender={typeChampSelectBox} />
                <Column dataField="quantiteMaximum" caption="Quantité Max." dataType="number" alignment={'left'}></Column>
                <Column dataField="ajoutAutomatique" caption="Ajout Automatique" dataType="boolean"></Column>
                <Column dataField="sejourComplet" caption="Séjour complet" dataType="boolean"></Column>
                <Column dataField="internet" caption="Internet" dataType="boolean"></Column>
                <Column dataField="logiciel" caption="Logiciel" dataType="boolean"></Column>
                <Column dataField="isDeleted" dataType="boolean" visible={false} />
              </DataGrid>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Forfaits">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedUnite || {} as IUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem>
              <DataGrid
                showBorders={true}
                allowColumnResizing={true}
                dataSource={selectedUnite.forfaits}
                allowColumnReordering={false}
                filterValue={dataGridFilterValue}
                height={"100%"}
                noDataText={"Aucune plage horaire"}
                onInitNewRow={(e: any) => {
                  e.data.idUnite = selectedUnite.idUnite;
                  e.data.isDeleted = false;
                }}
                onEditorPreparing={(e: any) => {
                  const originalOnValueChanged = e.editorOptions.onValueChanged;
                  e.editorOptions.onValueChanged = (args: any) => {
                    if (originalOnValueChanged) {
                      originalOnValueChanged(args);
                    }

                    const currentData = e.component.option("dataSource");
                    const rowIndex = e.rowIndex;
                    const rowData = currentData[rowIndex];

                    const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                    setSelectedDataRow(prev => ({
                      ...prev,
                      forfaits: updatedData
                    }));
                    setIsFieldModifier(true);
                  };
                }}
                onRowRemoving={async (e) => {
                  e.cancel = true;
                  const index = selectedUnite.forfaits.findIndex(item =>
                    item.idUniteForfait?.equals(e.data.idUniteForfait)
                  );
                  if (index !== -1) {
                    const updatedData = [...selectedUnite.forfaits];
                    updatedData[index] = { ...updatedData[index], isDeleted: true };
                    setSelectedDataRow(prev => ({
                      ...prev,
                      forfaits: updatedData
                    }));
                  }
                }}
              >
                <Editing
                  mode="cell"
                  allowUpdating={mode !== Mode.Lecture}
                  allowDeleting={mode !== Mode.Lecture}
                  allowAdding={mode !== Mode.Lecture}
                  newRowPosition={"first"}
                  startEditAction={"click"}
                />
                <Column dataField={"idPlanEntete"} dataType="string" caption={"Plan"} editorOptions={planParDefautGridLookupOptions}>
                  <Lookup {...planParDefautGridLookupOptions} />
                </Column>
                <Column dataField={"ordreAffichage"} dataType={"number"} caption={"Ordre d'affichage"} />
                <Column dataField="actif" dataType="boolean" caption={"Actif"} />
                <Column dataField="disponibleEnLigne" dataType="boolean" caption={"Disponible en ligne"} />
                <Column dataField="isDeleted" dataType="boolean" visible={false} />
              </DataGrid>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Services et équipements">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedUnite || {} as IUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <SimpleItem key={"frm-dimension-equipement"} dataField={"dimensionEquipement"} editorOptions={dimensionTerrainOptions}>
              <Label text={"Dimension d'équipement (min - max)"} />
            </SimpleItem>
            <SimpleItem key={"frm-entree-sortie"} dataField={"entreeSortie"} editorType={"dxCheckBox"} editorOptions={entreeSortieOptions}>
              <Label visible={false} />
            </SimpleItem>
            <GroupItem colCount={2} alignItemLabels={false}>
              <GroupItem caption={"Types de services disponibles pour l'unité"} colSpan={1}>
                <GroupItem>
                  <DataGrid
                    showBorders={true}
                    allowColumnResizing={true}
                    dataSource={selectedUnite.services}
                    allowColumnReordering={false}
                    filterValue={dataGridFilterValue}
                    height={"100%"}
                    noDataText={"Aucune plage horaire"}
                    onInitNewRow={(e: any) => {
                      e.data.idUnite = selectedUnite.idUnite;
                      e.data.isDeleted = false;
                      e.data.idUniteServiceType = e.data.idUniteServiceType ?? Guid.empty;
                    }}
                    onEditorPreparing={(e: any) => {
                      if (e.parentType === 'dataRow' && e.dataField === 'idUniteServiceType' && e.row?.data) {
                        setEditingUniteServiceRow(e.row.data);
                      }
                    }}
                    onRowUpdated={(e) => {
                      if (mode !== Mode.Lecture) {
                        syncUniteGridRows(e.component, 'services');
                      }
                    }}
                    onRowRemoving={async (e) => {
                      e.cancel = true;
                      const index = (selectedUnite?.services ?? []).findIndex(item =>
                        item.idUniteService?.equals(e.data.idUniteService)
                      );
                      if (index !== -1) {
                        const updatedData = [...selectedUnite.services];
                        updatedData[index] = { ...updatedData[index], isDeleted: true };
                        setSelectedDataRow(prev => ({
                          ...prev,
                          services: updatedData
                        }));
                      }
                    }}
                  >
                    <Editing
                      mode="cell"
                      allowUpdating={mode !== Mode.Lecture}
                      allowDeleting={mode !== Mode.Lecture}
                      allowAdding={mode !== Mode.Lecture}
                      newRowPosition={"first"}
                      startEditAction={"click"}
                    />
                    <Column dataField={"idUniteServiceType"} dataType="string" caption={"Service"} editorOptions={uniteServiceGridLookupOptions}>
                      <Lookup {...uniteServiceGridLookupOptions} />
                    </Column>
                    <Column dataField="isDeleted" dataType="boolean" visible={false} />
                  </DataGrid>
                </GroupItem>
              </GroupItem>
              <GroupItem caption={"Types d'équipement permis pour l'unité"} colSpan={1}>
                <GroupItem>
                  <DataGrid
                    showBorders={true}
                    allowColumnResizing={true}
                    dataSource={selectedUnite.equipements}
                    allowColumnReordering={false}
                    filterValue={dataGridFilterValue}
                    height={"100%"}
                    noDataText={"Aucune plage horaire"}
                    onInitNewRow={(e: any) => {
                      e.data.idUnite = selectedUnite.idUnite;
                      e.data.isDeleted = false;
                    }}
                    onEditorPreparing={(e: any) => {
                      if (e.parentType === 'dataRow' && e.dataField === 'idUniteEquipmentType' && e.row?.data) {
                        setEditingUniteEquipmentRow(e.row.data);
                      }
                    }}
                    onRowUpdated={() => {
                      if (mode !== Mode.Lecture) {
                        setIsFieldModifier(true);
                      }
                    }}
                    onRowRemoving={async (e) => {
                      e.cancel = true;
                      const index = (selectedUnite?.equipements ?? []).findIndex(item =>
                        item.idUniteEquipment?.equals(e.data.idUniteEquipment)
                      );
                      if (index !== -1) {
                        const updatedData = [...selectedUnite.equipements];
                        updatedData[index] = { ...updatedData[index], isDeleted: true };
                        setSelectedDataRow(prev => ({
                          ...prev,
                          equipements: updatedData
                        }));
                      }
                    }}
                  >
                    <Editing
                      mode="cell"
                      allowUpdating={mode !== Mode.Lecture}
                      allowDeleting={mode !== Mode.Lecture}
                      allowAdding={mode !== Mode.Lecture}
                      newRowPosition={"first"}
                      startEditAction={"click"}
                    />
                    <Column dataField={"idUniteEquipmentType"} dataType="string" caption={"Équipement"} editorOptions={uniteEquipementGridLookupOptions}>
                      <Lookup {...uniteEquipementGridLookupOptions} />
                    </Column>
                    <Column dataField="isDeleted" dataType="boolean" visible={false} />
                  </DataGrid>
                </GroupItem>
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Compteur">
          <div style={{ display: "flex" }}>
            <div style={{ width: "40%" }}>
              <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedUnite || {} as IUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
                <SimpleItem key={"frm-numero-compteur"} dataField={"numeroCompteur"} editorOptions={{ width: "60%" }}>
                  <Label text={"Numéro de compteur"} alignment='right' />
                </SimpleItem>
                <GroupItem caption={"Historique des lectures du compteur"}>
                  <GroupItem>
                    <DataGrid
                      showBorders={true}
                      allowColumnResizing={true}
                      dataSource={selectedUnite.compteurs}
                      allowColumnReordering={false}
                      filterValue={dataGridFilterValue}
                      height={"100%"}
                      noDataText={"Aucune plage horaire"}
                      onInitNewRow={(e: any) => {
                        e.data.idUnite = selectedUnite.idUnite;
                        e.data.isDeleted = false;
                      }}
                      onEditorPreparing={(e: any) => {
                        const originalOnValueChanged = e.editorOptions.onValueChanged;
                        e.editorOptions.onValueChanged = (args: any) => {
                          if (originalOnValueChanged) {
                            originalOnValueChanged(args);
                          }

                          const currentData = e.component.option("dataSource");
                          const rowIndex = e.rowIndex;
                          const rowData = currentData[rowIndex];

                          const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                          setSelectedDataRow(prev => ({
                            ...prev,
                            compteurs: updatedData
                          }));
                          setIsFieldModifier(true);
                        };
                      }}
                      onRowRemoving={async (e) => {
                        e.cancel = true;
                        const index = selectedUnite.compteurs.findIndex(item =>
                          item.idUniteCompteur?.equals(e.data.idUniteCompteur)
                        );
                        if (index !== -1) {
                          const updatedData = [...selectedUnite.compteurs];
                          updatedData[index] = { ...updatedData[index], isDeleted: true };
                          setSelectedDataRow(prev => ({
                            ...prev,
                            compteurs: updatedData
                          }));
                        }
                      }}
                    >
                      <Editing mode="cell" allowUpdating={mode !== Mode.Lecture} allowDeleting={mode !== Mode.Lecture} allowAdding={mode !== Mode.Lecture} newRowPosition={"first"} startEditAction={"click"} />
                      <Column dataField={"valeur"} caption={"Valeur"} dataType={"number"} alignment={'center'} />
                      <Column dataField={"dateLecture"} caption={"Date de lecture"} dataType={"date"} format={"yyyy-MM-dd"} alignment={'center'} />
                      <Column dataField="isDeleted" dataType="boolean" visible={false} />
                    </DataGrid>
                  </GroupItem>
                </GroupItem>
              </Form>
            </div>
          </div>
        </TabItem>
        <TabItem title="Internet">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedUnite || {} as IUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem colCount={11}>
              <GroupItem colSpan={3}>
                <SimpleItem key={"frm-disponible-en-ligne"} dataField={"disponibleEnLigne"} editorType={"dxCheckBox"} editorOptions={disponibleEnLigneOptions}>
                  <Label visible={false} />
                </SimpleItem>
                <SimpleItem key={"frm-afficher-calendrier-en-ligne"} dataField={"afficherCalendrierEnLigne"} editorType={"dxCheckBox"} editorOptions={afficherCalendrierOptions}>
                  <Label visible={false} />
                </SimpleItem>
                <SimpleItem key={"frm-description-web"} dataField={"descriptionWeb"} editorType={"dxTextArea"}
                  editorOptions={descriptionWebOptions}>
                  <Label text={"Description web française"} location={"top"} />
                </SimpleItem>
                <SimpleItem key={"frm-description-web-anglais"} dataField={"descriptionWebAnglais"}
                  editorType={"dxTextArea"} editorOptions={descriptionWebOptions}>
                  <Label text={"Description web anglaise"} location={"top"} />
                </SimpleItem>
              </GroupItem>
              <GroupItem colSpan={4} caption={"Restriction des groupes de personnes par tranches d'âges"}>
                <DataGrid
                  showBorders={true}
                  allowColumnResizing={true}
                  dataSource={selectedUnite.tranchesAge}
                  allowColumnReordering={false}
                  filterValue={dataGridFilterValue}
                  height={"100%"}
                  noDataText={"Aucune plage horaire"}
                  onInitNewRow={(e: any) => {
                    e.data.idUnite = selectedUnite.idUnite;
                    e.data.isDeleted = false;
                  }}
                  onEditorPreparing={(e: any) => {
                    if (e.parentType === 'dataRow' && e.dataField === 'idTrancheAge' && e.row?.data) {
                      setEditingUniteTrancheAgeRow(e.row.data);
                    }
                  }}
                  onRowUpdated={(e) => {
                    if (mode !== Mode.Lecture) {
                      syncUniteGridRows(e.component, 'tranchesAge');
                    }
                  }}
                  onRowRemoving={async (e) => {
                    e.cancel = true;
                    const index = selectedUnite.tranchesAge.findIndex(item =>
                      item === e.data
                      || (item.idUnitePersonneTrancheAge && e.data.idUnitePersonneTrancheAge?.equals(item.idUnitePersonneTrancheAge))
                    );
                    if (index !== -1) {
                      const updatedData = [...selectedUnite.tranchesAge];
                      updatedData[index] = { ...updatedData[index], isDeleted: true };
                      setSelectedDataRow(prev => ({
                        ...prev,
                        tranchesAge: updatedData
                      }));
                    }
                  }}
                >
                  <Editing mode="cell" allowUpdating={mode !== Mode.Lecture} allowDeleting={mode !== Mode.Lecture} allowAdding={mode !== Mode.Lecture} newRowPosition={"first"} startEditAction={"click"} />
                  <Column dataField={"groupe"} dataType={"number"} caption={"Groupe"} alignment='left' editorOptions={spinOptions} />
                  <Column dataField={"nombrePersonnes"} dataType={"number"} caption={"Nombre de personnes"} editorOptions={spinOptions} />
                  <Column dataField={"idTrancheAge"} dataType={"string"} caption={"Tranche d'âge"} editorOptions={trancheAgeGridLookupOptions}>
                    <Lookup {...trancheAgeGridLookupOptions} />
                  </Column>
                  <Column dataField="isDeleted" dataType="boolean" visible={false} />
                </DataGrid>
              </GroupItem>
              <GroupItem colSpan={4} caption={"Photos"}>
                <GroupItem colCount={4}>
                  <SimpleItem editorOptions={photo1Options} />
                  <SimpleItem editorOptions={photo2Options} />
                  <SimpleItem editorOptions={photo3Options} />
                  <SimpleItem editorOptions={photo4Options} />
                </GroupItem>
                <GroupItem>
                  <DataGrid showBorders={true} allowColumnResizing={true}></DataGrid>
                </GroupItem>
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Budget">
          <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedUnite || {} as IUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <SimpleItem key={"frm-annee"} editorType={"dxNumberBox"} dataField={"annee"}
              editorOptions={anneeOptions}>
              <Label text={"Année"} />
            </SimpleItem>
            <GroupItem>
              <SimpleItem>
                <div style={{ textAlign: "center", marginTop: "1.3em" }}>
                  <label style={{ fontWeight: "bold" }}>Nuitées</label>
                </div>
              </SimpleItem>
              <GroupItem>
                <DataGrid
                  showBorders={true}
                  allowColumnResizing={true}
                  dataSource={selectedUnite.budgetNuitees}
                  allowColumnReordering={false}
                  filterValue={dataGridFilterValue}
                  height={"100%"}
                  noDataText={"Aucun budget"}
                  onInitNewRow={(e: any) => {
                    e.data.idUnite = selectedUnite.idUnite;
                    e.data.isDeleted = false;
                  }}
                  onEditorPreparing={(e: any) => {
                    const originalOnValueChanged = e.editorOptions.onValueChanged;
                    e.editorOptions.onValueChanged = (args: any) => {
                      if (originalOnValueChanged) {
                        originalOnValueChanged(args);
                      }

                      const currentData = e.component.option("dataSource");
                      const rowIndex = e.rowIndex;
                      const rowData = currentData[rowIndex];

                      const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                      setSelectedDataRow(prev => ({
                        ...prev,
                        budgetNuitees: updatedData
                      }));
                      setIsFieldModifier(true);
                    };
                  }}
                  onRowRemoving={async (e) => {
                    e.cancel = true;
                    const index = selectedUnite.budgetNuitees.findIndex(item =>
                      item.idUniteBudgetNuitee?.equals(e.data.idUniteBudgetNuitee)
                    );
                    if (index !== -1) {
                      const updatedData = [...selectedUnite.budgetNuitees];
                      updatedData[index] = { ...updatedData[index], isDeleted: true };
                      setSelectedDataRow(prev => ({
                        ...prev,
                        budgetNuitees: updatedData
                      }));
                    }
                  }}
                >
                  <Editing
                    mode="cell"
                    allowUpdating={mode !== Mode.Lecture}
                    allowDeleting={mode !== Mode.Lecture}
                    allowAdding={mode !== Mode.Lecture}
                    newRowPosition={"first"}
                    startEditAction={"click"}
                  />
                  <Column width={130} dataField={"idPlanEntete"} dataType="string" caption={"Plan"}>
                    <Lookup {...planParDefautEditorOption} />
                  </Column>
                  <Column dataField={"annee"} caption={"année"} groupIndex={0} />
                  <Column width={130} dataField={"idPlanDetailType"} caption={"Type (Facultatif)"} cellRender={typeBudgetNuitSelectBox} />
                  <Column dataField={"nuiteesJanvier"} caption={"Janvier"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesFevrier"} caption={"Février"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesMars"} caption={"Mars"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesAvril"} caption={"Avril"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesMai"} caption={"Mai"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesJuin"} caption={"Juin"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesJuillet"} caption={"Juillet"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesAout"} caption={"Août"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesSeptembre"} caption={"Septembre"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesOctobre"} caption={"Octobre"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesNovembre"} caption={"Novembre"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField={"nuiteesDecembre"} caption={"Décembre"} editorOptions={nuiteesEditorOptions} />
                  <Column dataField="isDeleted" dataType="boolean" visible={false} />
                </DataGrid>
              </GroupItem>
            </GroupItem>
            <GroupItem>
              <SimpleItem>
                <div style={{ textAlign: "center", marginTop: "1.3em" }}>
                  <label style={{ textAlign: "center", fontWeight: "bold" }}>Revenus</label>
                </div>
              </SimpleItem>
              <GroupItem>
                <DataGrid
                  showBorders={true}
                  allowColumnResizing={true}
                  dataSource={selectedUnite.budgetRevenus}
                  allowColumnReordering={false}
                  filterValue={dataGridFilterValue}
                  height={"100%"}
                  noDataText={"Aucun budget"}
                  onInitNewRow={(e: any) => {
                    e.data.idUnite = selectedUnite.idUnite;
                    e.data.isDeleted = false;
                  }}
                  onEditorPreparing={(e: any) => {
                    const originalOnValueChanged = e.editorOptions.onValueChanged;
                    e.editorOptions.onValueChanged = (args: any) => {
                      if (originalOnValueChanged) {
                        originalOnValueChanged(args);
                      }

                      const currentData = e.component.option("dataSource");
                      const rowIndex = e.rowIndex;
                      const rowData = currentData[rowIndex];

                      const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                      setSelectedDataRow(prev => ({
                        ...prev,
                        budgetRevenus: updatedData
                      }));
                      setIsFieldModifier(true);
                    };
                  }}
                  onRowRemoving={async (e) => {
                    e.cancel = true;
                    const index = selectedUnite.budgetRevenus.findIndex(item =>
                      item.idUniteBudgetRevenus?.equals(e.data.idUniteBudgetRevenus)
                    );
                    if (index !== -1) {
                      const updatedData = [...selectedUnite.budgetRevenus];
                      updatedData[index] = { ...updatedData[index], isDeleted: true };
                      setSelectedDataRow(prev => ({
                        ...prev,
                        budgetRevenus: updatedData
                      }));
                    }
                  }}
                >
                  <Editing
                    mode="cell"
                    allowUpdating={mode !== Mode.Lecture}
                    allowDeleting={mode !== Mode.Lecture}
                    allowAdding={mode !== Mode.Lecture}
                    newRowPosition={"first"}
                    startEditAction={"click"}
                  />
                  <Column width={130} dataField={"idPlanEntete"} dataType="string" caption={"Plan"}>
                    <Lookup {...planParDefautEditorOption} />
                  </Column>
                  <Column dataField={"annee"} caption={"année"} groupIndex={0} />
                  <Column width={130} dataField={"idPlanDetailType"} caption={"Type (Facultatif)"} cellRender={typeBudgetRevenuSelectBox} />
                  <Column dataField={"revenusJanvier"} caption={"Janvier"} />
                  <Column dataField={"revenusFevrier"} caption={"Février"} />
                  <Column dataField={"revenusMars"} caption={"Mars"} />
                  <Column dataField={"revenusAvril"} caption={"Avril"} />
                  <Column dataField={"revenusMai"} caption={"Mai"} />
                  <Column dataField={"revenusJuin"} caption={"Juin"} />
                  <Column dataField={"revenusJuillet"} caption={"Juillet"} />
                  <Column dataField={"revenusAout"} caption={"Août"} />
                  <Column dataField={"revenusSeptembre"} caption={"Septembre"} />
                  <Column dataField={"revenusOctobre"} caption={"Octobre"} />
                  <Column dataField={"revenusNovembre"} caption={"Novembre"} />
                  <Column dataField={"revenusDecembre"} caption={"Décembre"} />
                  <Column dataField="isDeleted" dataType="boolean" visible={false} />
                </DataGrid>
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedUnite || {} as IGestionUnite} /></TabItem>
      </FormListBaseTab>
    </FormListBase>
  );
}